import { supabase } from '../lib/supabase';

export const remediationService = {
  // ============================================================================
  // COMPLIANCE VIOLATIONS
  // ============================================================================

  async getAllViolations() {
    const { data, error } = await supabase
      ?.from('compliance_violations')
      ?.select('*')
      ?.order('identified_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationType: row?.violation_type,
      severity: row?.severity,
      violationStatus: row?.violation_status,
      regulationReference: row?.regulation_reference,
      description: row?.description,
      affectedEmployeeIds: row?.affected_employee_ids || [],
      affectedCount: row?.affected_count,
      department: row?.department,
      estimatedCost: row?.estimated_cost,
      correctiveAction: row?.corrective_action,
      identifiedAt: row?.identified_at,
      resolvedAt: row?.resolved_at,
      identifiedBy: row?.identified_by,
      assignedTo: row?.assigned_to,
      metadata: row?.metadata,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getViolationById(id) {
    const { data, error } = await supabase
      ?.from('compliance_violations')
      ?.select('*')
      ?.eq('id', id)
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      violationType: data?.violation_type,
      severity: data?.severity,
      violationStatus: data?.violation_status,
      regulationReference: data?.regulation_reference,
      description: data?.description,
      affectedEmployeeIds: data?.affected_employee_ids || [],
      affectedCount: data?.affected_count,
      department: data?.department,
      estimatedCost: data?.estimated_cost,
      correctiveAction: data?.corrective_action,
      identifiedAt: data?.identified_at,
      resolvedAt: data?.resolved_at,
      identifiedBy: data?.identified_by,
      assignedTo: data?.assigned_to,
      metadata: data?.metadata
    };
  },

  async getViolationsBySeverity(severity) {
    const { data, error } = await supabase
      ?.from('compliance_violations')
      ?.select('*')
      ?.eq('severity', severity)
      ?.order('identified_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationType: row?.violation_type,
      severity: row?.severity,
      violationStatus: row?.violation_status,
      description: row?.description,
      affectedCount: row?.affected_count,
      estimatedCost: row?.estimated_cost,
      identifiedAt: row?.identified_at
    })) || [];
  },

  async getViolationsByStatus(status) {
    const { data, error } = await supabase
      ?.from('compliance_violations')
      ?.select('*')
      ?.eq('violation_status', status)
      ?.order('identified_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationType: row?.violation_type,
      severity: row?.severity,
      violationStatus: row?.violation_status,
      description: row?.description,
      affectedCount: row?.affected_count,
      estimatedCost: row?.estimated_cost,
      identifiedAt: row?.identified_at
    })) || [];
  },

  async createViolation(violation) {
    const { data, error } = await supabase
      ?.from('compliance_violations')
      ?.insert({
        violation_type: violation?.violationType,
        severity: violation?.severity,
        violation_status: violation?.violationStatus || 'identified',
        regulation_reference: violation?.regulationReference,
        description: violation?.description,
        affected_employee_ids: violation?.affectedEmployeeIds || [],
        affected_count: violation?.affectedCount || 0,
        department: violation?.department,
        estimated_cost: violation?.estimatedCost || 0,
        corrective_action: violation?.correctiveAction,
        identified_by: violation?.identifiedBy,
        assigned_to: violation?.assignedTo,
        metadata: violation?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      violationType: data?.violation_type,
      severity: data?.severity,
      violationStatus: data?.violation_status
    };
  },

  async updateViolationStatus(id, status, updates = {}) {
    const updateData = {
      violation_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'completed') {
      updateData.resolved_at = new Date()?.toISOString();
    }

    if (updates?.assignedTo) {
      updateData.assigned_to = updates?.assignedTo;
    }

    const { data, error } = await supabase
      ?.from('compliance_violations')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      violationStatus: data?.violation_status,
      resolvedAt: data?.resolved_at
    };
  },

  // ============================================================================
  // REMEDIATION ACTIONS
  // ============================================================================

  async getRemediationActions() {
    const { data, error } = await supabase
      ?.from('remediation_actions')
      ?.select(`
        *,
        compliance_violations (
          violation_type,
          severity,
          description
        )
      `)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationId: row?.violation_id,
      actionType: row?.action_type,
      remediationStatus: row?.remediation_status,
      priority: row?.priority,
      description: row?.description,
      implementationSteps: row?.implementation_steps || [],
      backpayAmount: row?.backpay_amount,
      affectedEmployeeIds: row?.affected_employee_ids || [],
      affectedCount: row?.affected_count,
      scheduledDate: row?.scheduled_date,
      startedAt: row?.started_at,
      completedAt: row?.completed_at,
      assignedTo: row?.assigned_to,
      notes: row?.notes,
      metadata: row?.metadata,
      violation: row?.compliance_violations ? {
        violationType: row?.compliance_violations?.violation_type,
        severity: row?.compliance_violations?.severity,
        description: row?.compliance_violations?.description
      } : null,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getRemediationActionsByViolation(violationId) {
    const { data, error } = await supabase
      ?.from('remediation_actions')
      ?.select('*')
      ?.eq('violation_id', violationId)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      actionType: row?.action_type,
      remediationStatus: row?.remediation_status,
      priority: row?.priority,
      description: row?.description,
      backpayAmount: row?.backpay_amount,
      scheduledDate: row?.scheduled_date,
      assignedTo: row?.assigned_to
    })) || [];
  },

  async createRemediationAction(action) {
    const { data, error } = await supabase
      ?.from('remediation_actions')
      ?.insert({
        violation_id: action?.violationId,
        action_type: action?.actionType,
        remediation_status: action?.remediationStatus || 'pending',
        priority: action?.priority,
        description: action?.description,
        implementation_steps: action?.implementationSteps || [],
        backpay_amount: action?.backpayAmount || 0,
        affected_employee_ids: action?.affectedEmployeeIds || [],
        affected_count: action?.affectedCount || 0,
        scheduled_date: action?.scheduledDate,
        assigned_to: action?.assignedTo,
        notes: action?.notes,
        metadata: action?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      violationId: data?.violation_id,
      remediationStatus: data?.remediation_status
    };
  },

  async updateRemediationStatus(id, status, updates = {}) {
    const updateData = {
      remediation_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'in_progress' && !updates?.startedAt) {
      updateData.started_at = new Date()?.toISOString();
    }

    if (status === 'completed') {
      updateData.completed_at = new Date()?.toISOString();
    }

    if (updates?.notes) {
      updateData.notes = updates?.notes;
    }

    const { data, error } = await supabase
      ?.from('remediation_actions')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      remediationStatus: data?.remediation_status,
      completedAt: data?.completed_at
    };
  },

  // ============================================================================
  // VIOLATION CORRECTIONS
  // ============================================================================

  async getViolationCorrections() {
    const { data, error } = await supabase
      ?.from('violation_corrections')
      ?.select(`
        *,
        compliance_violations (
          violation_type,
          severity,
          description
        ),
        employees (
          employee_number,
          first_name,
          last_name,
          department
        )
      `)
      ?.order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      violationId: row?.violation_id,
      employeeId: row?.employee_id,
      correctionType: row?.correction_type,
      currentValue: row?.current_value,
      correctedValue: row?.corrected_value,
      calculationMethod: row?.calculation_method,
      backpayAmount: row?.backpay_amount,
      priority: row?.priority,
      implementationStatus: row?.implementation_status,
      implementedAt: row?.implemented_at,
      payrollRunId: row?.payroll_run_id,
      notes: row?.notes,
      aiConfidenceScore: row?.ai_confidence_score,
      reviewedBy: row?.reviewed_by,
      reviewedAt: row?.reviewed_at,
      metadata: row?.metadata,
      violation: row?.compliance_violations ? {
        violationType: row?.compliance_violations?.violation_type,
        severity: row?.compliance_violations?.severity,
        description: row?.compliance_violations?.description
      } : null,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name,
        department: row?.employees?.department
      } : null,
      createdAt: row?.created_at,
      updatedAt: row?.updated_at
    })) || [];
  },

  async getCorrectionsByViolation(violationId) {
    const { data, error } = await supabase
      ?.from('violation_corrections')
      ?.select(`
        *,
        employees (
          employee_number,
          first_name,
          last_name
        )
      `)
      ?.eq('violation_id', violationId)
      ?.order('priority', { ascending: true });
    
    if (error) throw error;
    
    return data?.map(row => ({
      id: row?.id,
      employeeId: row?.employee_id,
      correctionType: row?.correction_type,
      currentValue: row?.current_value,
      correctedValue: row?.corrected_value,
      backpayAmount: row?.backpay_amount,
      priority: row?.priority,
      implementationStatus: row?.implementation_status,
      employee: row?.employees ? {
        employeeNumber: row?.employees?.employee_number,
        firstName: row?.employees?.first_name,
        lastName: row?.employees?.last_name
      } : null
    })) || [];
  },

  async createViolationCorrection(correction) {
    const { data, error } = await supabase
      ?.from('violation_corrections')
      ?.insert({
        violation_id: correction?.violationId,
        employee_id: correction?.employeeId,
        correction_type: correction?.correctionType,
        current_value: correction?.currentValue,
        corrected_value: correction?.correctedValue,
        calculation_method: correction?.calculationMethod,
        backpay_amount: correction?.backpayAmount || 0,
        priority: correction?.priority,
        implementation_status: correction?.implementationStatus || 'pending',
        notes: correction?.notes,
        ai_confidence_score: correction?.aiConfidenceScore,
        metadata: correction?.metadata
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      violationId: data?.violation_id,
      employeeId: data?.employee_id,
      implementationStatus: data?.implementation_status
    };
  },

  async updateCorrectionStatus(id, status, updates = {}) {
    const updateData = {
      implementation_status: status,
      updated_at: new Date()?.toISOString()
    };

    if (status === 'completed') {
      updateData.implemented_at = new Date()?.toISOString();
    }

    if (updates?.payrollRunId) {
      updateData.payroll_run_id = updates?.payrollRunId;
    }

    if (updates?.reviewedBy) {
      updateData.reviewed_by = updates?.reviewedBy;
      updateData.reviewed_at = new Date()?.toISOString();
    }

    const { data, error } = await supabase
      ?.from('violation_corrections')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      implementationStatus: data?.implementation_status,
      implementedAt: data?.implemented_at
    };
  },

  // ============================================================================
  // STATISTICS & ANALYTICS
  // ============================================================================

  async getRemediationStatistics() {
    const { data, error } = await supabase
      ?.rpc('get_remediation_statistics');
    
    if (error) throw error;
    
    return data?.[0] ? {
      totalViolations: data?.[0]?.total_violations,
      criticalViolations: data?.[0]?.critical_violations,
      pendingRemediations: data?.[0]?.pending_remediations,
      inProgressRemediations: data?.[0]?.in_progress_remediations,
      completedRemediations: data?.[0]?.completed_remediations,
      totalBackpayRequired: data?.[0]?.total_backpay_required,
      totalBackpayPaid: data?.[0]?.total_backpay_paid
    } : {
      totalViolations: 0,
      criticalViolations: 0,
      pendingRemediations: 0,
      inProgressRemediations: 0,
      completedRemediations: 0,
      totalBackpayRequired: 0,
      totalBackpayPaid: 0
    };
  },

  async calculateViolationBackpay(violationId) {
    const { data, error } = await supabase
      ?.rpc('calculate_violation_backpay', { violation_uuid: violationId });
    
    if (error) throw error;
    
    return parseFloat(data || 0);
  }
};

export default remediationService;